import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import useApi from '../../hooks/useApi';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const BackRow = styled.div`
  margin-bottom: 1rem;
`;

const BackLink = styled(Link)`
  font-size: 0.88rem;
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Article = styled.article`
  margin-top: 0.5rem;
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
`;

const AdminBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const DangerButton = styled.button`
  border: 1px solid #fecaca;
  background: #fef2f2;
  border-radius: 0.55rem;
  padding: 0.35rem 0.75rem;
  font-weight: 700;
  font-size: 0.82rem;
  cursor: pointer;
  color: #b91c1c;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AuthorActionBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
  margin-bottom: 0.65rem;
`;

const TitleInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  margin: 0 0 0.75rem;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
`;

const PostEditTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 200px;
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  line-height: 1.65;
  color: #1f2937;
  border: 1px solid #cbd5e1;
  border-radius: 0.55rem;
  padding: 0.55rem 0.65rem;
  resize: vertical;
`;

const PostFormError = styled.p`
  margin: 0 0 0.65rem;
  color: #b91c1c;
  font-size: 0.86rem;
`;

const Title = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
  color: ${({ $removed }) => ($removed ? '#94a3b8' : '#0f172a')};
  line-height: 1.35;
  font-style: ${({ $removed }) => ($removed ? 'italic' : 'normal')};
`;

const AuthorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
`;

const Avatar = styled.span`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  background: rgba(248, 250, 252, 0.95);
`;

const AuthorMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  min-width: 0;
`;

const AuthorName = styled.span`
  font-weight: 800;
  color: #0f172a;
`;

const AuthorLogin = styled.span`
  font-size: 0.78rem;
  color: #64748b;
`;

const MetaSep = styled.span`
  color: #cbd5e1;
  font-weight: 500;
  user-select: none;
`;

const DateLine = styled.p`
  margin: 0 0 1rem;
  font-size: 0.82rem;
  color: #64748b;
`;

const ViewCountLine = styled.span`
  margin-left: 0.65rem;
  padding-left: 0.65rem;
  border-left: 1px solid #e2e8f0;
  font-variant-numeric: tabular-nums;
`;

const LikeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 1.15rem 0 0;
`;

const LikeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid ${({ $active }) => ($active ? '#fda4af' : '#e2e8f0')};
  background: ${({ $active }) => ($active ? '#fff1f2' : '#fff')};
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.86rem;
  font-weight: 700;
  cursor: pointer;
  color: ${({ $active }) => ($active ? '#e11d48' : '#64748b')};
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: #fda4af;
    background: #fff1f2;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const LikeHeart = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const LikeCountNum = styled.span`
  font-variant-numeric: tabular-nums;
`;

const Body = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.95rem;
  line-height: 1.65;
  color: ${({ $removed }) => ($removed ? '#94a3b8' : '#1f2937')};
  font-style: ${({ $removed }) => ($removed ? 'italic' : 'normal')};
`;

const CommentsSection = styled.section`
  margin-top: 1.75rem;
  padding-top: 1.35rem;
  border-top: 1px solid #e8ecf1;
`;

const CommentsTitle = styled.h3`
  margin: 0 0 0.85rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
`;

const CommentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 0.85rem;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #e8ecf1;
  background: #fafbfc;
`;

const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 88px;
  border: 1px solid #d8dee9;
  border-radius: 0.6rem;
  padding: 0.6rem 0.7rem;
  font-size: 0.9rem;
  resize: vertical;
  background: #fff;
  color: #1e293b;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 0.6rem;
  padding: 0.45rem 0.9rem;
  font-weight: 700;
  font-size: 0.86rem;
  cursor: pointer;
  background: rgba(99, 102, 241, 0.95);
  color: #f8fafc;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid #d8dee9;
  border-radius: 0.5rem;
  padding: 0.32rem 0.65rem;
  font-weight: 600;
  font-size: 0.78rem;
  cursor: pointer;
  background: #fff;
  color: #64748b;
  &:hover {
    border-color: #c7d2fe;
    color: #4f46e5;
    background: #fafbff;
  }
`;

const CommentList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const CommentItem = styled.li`
  padding: 0;
`;

const CommentInner = styled.div`
  padding-left: ${({ $depth }) => `${Math.min($depth, 6) * 0.85}rem`};
  border-left: ${({ $depth }) => ($depth > 0 ? '2px solid #e0e7ff' : 'none')};
`;

const CommentCard = styled.div`
  border: 1px solid #e8ecf1;
  border-radius: 0.75rem;
  background: #fafbfc;
  padding: 0.85rem 1rem;
`;

const CommentGrid = styled.div`
  display: grid;
  grid-template-columns: 2.35rem minmax(0, 1fr) auto;
  grid-template-areas:
    'cgav cgauth cgaside'
    'cgav cgbody cgbody';
  column-gap: 0.65rem;
  row-gap: 0.35rem;
  align-items: start;
`;

const CommentGridAv = styled.div`
  grid-area: cgav;
  justify-self: start;
`;

const CommentGridAuth = styled.div`
  grid-area: cgauth;
  min-width: 0;
`;

const CommentGridAside = styled.div`
  grid-area: cgaside;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.4rem;
`;

const SmallAvatar = styled.span`
  width: 2.35rem;
  height: 2.35rem;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  background: #fff;
  flex-shrink: 0;
`;

const AuthorStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  min-width: 0;
`;

const CommentBody = styled.div`
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${({ $removed }) => ($removed ? '#94a3b8' : '#334155')};
  font-style: ${({ $removed }) => ($removed ? 'italic' : 'normal')};
`;

const CommentGridBody = styled(CommentBody)`
  grid-area: cgbody;
  min-width: 0;
  padding-top: 0.15rem;
`;

const InlineReplyBox = styled.form`
  margin-top: 0.65rem;
  padding: 0.75rem 0.85rem;
  border-radius: 0.65rem;
  border: 1px solid #c7d2fe;
  background: #f8f9ff;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const InlineReplyTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: #4f46e5;
`;

const CompactTextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  border: 1px solid #d8dee9;
  border-radius: 0.55rem;
  padding: 0.5rem 0.65rem;
  font-size: 0.875rem;
  resize: vertical;
  background: #fff;
  color: #1e293b;
`;

const CommentDate = styled.span`
  font-size: 0.72rem;
  color: #94a3b8;
  white-space: nowrap;
`;

const ErrorText = styled.p`
  margin: 0.75rem 0 0;
  color: #b91c1c;
  font-size: 0.9rem;
`;

const EmptyComments = styled.p`
  margin: 0;
  padding: 0.5rem 0 0.25rem;
  color: #64748b;
  font-size: 0.88rem;
`;

const ReplyBottomHint = styled.div`
  margin-top: 0.85rem;
  padding: 0.75rem 1rem;
  border-radius: 0.65rem;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  font-size: 0.82rem;
  color: #475569;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.login_id ? parsed : null;
  } catch {
    return null;
  }
};

const formatDt = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ko-KR', { dateStyle: 'medium', timeStyle: 'short' });
};

const buildCommentTree = (flat) => {
  const byParent = new Map();
  flat.forEach((c) => {
    const pid = c.parent_comment_id ?? null;
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(c);
  });
  const walk = (parentId, depth) => {
    const kids = byParent.get(parentId) || [];
    return kids.flatMap((node) => [{ node, depth }, ...walk(node.id, depth + 1)]);
  };
  return walk(null, 0);
};

const isBoardModerator = (roles = []) =>
  roles.includes('teacher') || roles.includes('admin') || roles.includes('super_admin');

const canCommentStudentBoard = (roles = []) =>
  ['student', 'parent', 'teacher', 'admin', 'super_admin'].some((r) => roles.includes(r));

const samePortalUserId = (portalUserId, authorUserId) => {
  if (portalUserId == null || authorUserId == null) return false;
  return Number(portalUserId) === Number(authorUserId);
};

const PortalBoardDetailPage = () => {
  const { postId } = useParams();
  const { request, loading } = useApi();
  const portalUser = useMemo(() => readPortalUser(), []);
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [postEditing, setPostEditing] = useState(false);
  const [editPostTitle, setEditPostTitle] = useState('');
  const [editPostContent, setEditPostContent] = useState('');
  const [postFormError, setPostFormError] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentDraft, setEditCommentDraft] = useState('');
  const viewCountGateRef = useRef({ postKey: '', counted: false, pendingBump: false });
  /** 초기 GET이 POST /like 보다 늦게 끝나면 오래된 like_count로 덮어쓰는 것을 막음 */
  const detailFetchGenRef = useRef(0);

  const moderator = isBoardModerator(portalUser?.roles || []);
  const canComment = canCommentStudentBoard(portalUser?.roles || []);

  const load = useCallback(
    async ({ recordView = true } = {}) => {
      if (!portalUser?.login_id || !postId) return;
      const fetchGen = ++detailFetchGenRef.current;
      let bumpView = recordView;
      let lockedPendingBump = false;
      if (bumpView) {
        const key = `${postId}:${portalUser.login_id}`;
        const g = viewCountGateRef.current;
        if (g.postKey !== key) {
          viewCountGateRef.current = { postKey: key, counted: false, pendingBump: false };
        }
        const g2 = viewCountGateRef.current;
        if (g2.counted || g2.pendingBump) {
          bumpView = false;
        } else {
          g2.pendingBump = true;
          lockedPendingBump = true;
        }
      }
      setError('');
      try {
        const qs = new URLSearchParams({
          login_id: portalUser.login_id,
          record_view: bumpView ? 'true' : 'false',
        });
        const data = await request({
          endpoint: `${BACKEND_BASE_URL}/api/student-board/posts/${encodeURIComponent(postId)}?${qs.toString()}`,
        });
        if (fetchGen !== detailFetchGenRef.current) {
          return;
        }
        setPost(data?.post || null);
        setComments(data?.comments || []);
        if (bumpView) {
          viewCountGateRef.current.counted = true;
        }
      } catch (e) {
        if (fetchGen !== detailFetchGenRef.current) {
          return;
        }
        setPost(null);
        setComments([]);
        setError(e?.responseData?.message || '게시글을 불러오지 못했습니다.');
      } finally {
        if (lockedPendingBump) {
          viewCountGateRef.current.pendingBump = false;
        }
      }
    },
    [postId, portalUser?.login_id, request],
  );

  useEffect(() => {
    load({ recordView: true });
  }, [load]);

  const threaded = useMemo(() => buildCommentTree(comments), [comments]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setCommentError('');
    const text = commentText.trim();
    if (!text) return;
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/student-board/posts/${encodeURIComponent(postId)}/comments`,
        method: 'POST',
        body: {
          login_id: portalUser.login_id,
          content: text,
          parent_comment_id: replyTo,
        },
      });
      setCommentText('');
      setReplyTo(null);
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 등록에 실패했습니다.');
    }
  };

  const handleModeratePost = async () => {
    if (!window.confirm('이 게시글을 삭제 처리할까요?')) return;
    setCommentError('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/student-board/posts/${encodeURIComponent(postId)}/moderate`,
        method: 'POST',
        body: { login_id: portalUser.login_id },
      });
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '처리에 실패했습니다.');
    }
  };

  const handleModerateComment = async (commentId) => {
    if (!window.confirm('이 댓글을 삭제 처리할까요?')) return;
    setCommentError('');
    try {
      await request({
        endpoint: `${BACKEND_BASE_URL}/api/student-board/comments/${encodeURIComponent(commentId)}/moderate`,
        method: 'POST',
        body: { login_id: portalUser.login_id },
      });
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '처리에 실패했습니다.');
    }
  };

  const beginPostEdit = () => {
    if (!post) return;
    setPostFormError('');
    setEditPostTitle(post.title || '');
    setEditPostContent(post.content || '');
    setPostEditing(true);
    setEditingCommentId(null);
    setReplyTo(null);
    setCommentText('');
  };

  const cancelPostEdit = () => {
    setPostEditing(false);
    setPostFormError('');
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    setPostFormError('');
    const title = editPostTitle.trim();
    const content = editPostContent.trim();
    if (!title || !content) {
      setPostFormError('제목과 내용을 모두 입력해 주세요.');
      return;
    }
    try {
      await request({
        method: 'PUT',
        endpoint: `${BACKEND_BASE_URL}/api/student-board/posts/${encodeURIComponent(postId)}`,
        body: {
          login_id: portalUser.login_id,
          title,
          content,
        },
      });
      setPostEditing(false);
      await load({ recordView: false });
    } catch (err) {
      setPostFormError(err?.responseData?.message || '게시글 수정에 실패했습니다.');
    }
  };

  const beginCommentEdit = (c) => {
    setCommentError('');
    setEditingCommentId(c.id);
    setEditCommentDraft(c.content || '');
    setReplyTo(null);
    setCommentText('');
  };

  const cancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditCommentDraft('');
    setCommentError('');
  };

  const handleSaveComment = async (e, commentId) => {
    e.preventDefault();
    setCommentError('');
    const text = editCommentDraft.trim();
    if (!text) return;
    try {
      await request({
        method: 'PUT',
        endpoint: `${BACKEND_BASE_URL}/api/student-board/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id, content: text },
      });
      setEditingCommentId(null);
      setEditCommentDraft('');
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 수정에 실패했습니다.');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('이 댓글을 삭제할까요?')) return;
    setCommentError('');
    try {
      await request({
        method: 'DELETE',
        endpoint: `${BACKEND_BASE_URL}/api/student-board/comments/${encodeURIComponent(commentId)}`,
        body: { login_id: portalUser.login_id },
      });
      if (editingCommentId === commentId) {
        setEditingCommentId(null);
        setEditCommentDraft('');
      }
      await load({ recordView: false });
    } catch (err) {
      setCommentError(err?.responseData?.message || '댓글 삭제에 실패했습니다.');
    }
  };

  const handleToggleLike = async () => {
    if (!post || post.is_moderation_removed) return;
    setCommentError('');
    try {
      const data = await request({
        method: 'POST',
        endpoint: `${BACKEND_BASE_URL}/api/student-board/posts/${encodeURIComponent(postId)}/like`,
        body: { login_id: portalUser.login_id },
      });
      detailFetchGenRef.current += 1;
      setPost((prev) =>
        prev
          ? {
              ...prev,
              like_count: Number.isFinite(Number(data?.like_count)) ? Number(data.like_count) : prev.like_count,
              liked_by_me: Boolean(data?.liked_by_me),
            }
          : prev,
      );
    } catch (err) {
      setCommentError(err?.responseData?.message || '좋아요 처리에 실패했습니다.');
    }
  };

  if (!post && !error) {
    return (
      <Wrapper>
        <BackRow>
          <BackLink to="/portal/board">← 목록</BackLink>
        </BackRow>
        <SectionHeading eyebrow="BOARD" title="학생 자유 게시판" />
        <p style={{ color: '#64748b' }}>{loading ? '불러오는 중...' : ''}</p>
      </Wrapper>
    );
  }

  if (error && !post) {
    return (
      <Wrapper>
        <BackRow>
          <BackLink to="/portal/board">← 목록</BackLink>
        </BackRow>
        <SectionHeading eyebrow="BOARD" title="학생 자유 게시판" />
        <ErrorText>{error}</ErrorText>
      </Wrapper>
    );
  }

  const removed = Boolean(post?.is_moderation_removed);
  const isPostAuthor = samePortalUserId(portalUser?.id, post?.author?.user_id);

  const authorBlock = (
    <>
      <AuthorWrap>
        <Avatar aria-hidden>{post.author?.profile_emoji || '🙂'}</Avatar>
        <AuthorMeta>
          {(() => {
            const { primary, secondary } = getNoticeAuthorDisplay(post.author);
            return (
              <>
                <AuthorName>{primary}</AuthorName>
                {secondary ? (
                  <>
                    <MetaSep aria-hidden>·</MetaSep>
                    <AuthorLogin>{secondary}</AuthorLogin>
                  </>
                ) : null}
              </>
            );
          })()}
        </AuthorMeta>
      </AuthorWrap>
      <DateLine>
        작성 {formatDt(post.created_at)}
        <ViewCountLine>조회 {post.view_count ?? 0}</ViewCountLine>
      </DateLine>
    </>
  );

  const likeBlock =
    !post.is_moderation_removed ? (
      <LikeRow>
        <LikeButton
          type="button"
          $active={Boolean(post.liked_by_me)}
          disabled={loading}
          onClick={handleToggleLike}
          aria-pressed={Boolean(post.liked_by_me)}
          aria-label={post.liked_by_me ? '좋아요 취소' : '좋아요'}
        >
          <LikeHeart aria-hidden>{post.liked_by_me ? '♥' : '♡'}</LikeHeart>
          <span>좋아요</span>
          <LikeCountNum>{post.like_count ?? 0}</LikeCountNum>
        </LikeButton>
      </LikeRow>
    ) : null;

  return (
    <Wrapper>
      <BackRow>
        <BackLink to="/portal/board">← 게시판 목록</BackLink>
      </BackRow>

      <Article>
        {moderator && !removed ? (
          <AdminBar>
            <DangerButton type="button" disabled={loading} onClick={handleModeratePost}>
              게시글 삭제
            </DangerButton>
          </AdminBar>
        ) : null}
        {isPostAuthor && !removed && !postEditing ? (
          <AuthorActionBar>
            <GhostButton type="button" disabled={loading} onClick={beginPostEdit}>
              수정
            </GhostButton>
          </AuthorActionBar>
        ) : null}
        {postEditing ? (
          <form onSubmit={handleSavePost}>
            <TitleInput
              value={editPostTitle}
              onChange={(ev) => setEditPostTitle(ev.target.value)}
              maxLength={200}
              placeholder="제목"
              disabled={loading}
              aria-label="게시글 제목"
            />
            {authorBlock}
            <PostEditTextArea
              value={editPostContent}
              onChange={(ev) => setEditPostContent(ev.target.value)}
              disabled={loading}
              aria-label="게시글 내용"
            />
            {postFormError ? <PostFormError>{postFormError}</PostFormError> : null}
            <ButtonRow>
              <PrimaryButton type="submit" disabled={loading}>
                저장
              </PrimaryButton>
              <GhostButton type="button" disabled={loading} onClick={cancelPostEdit}>
                취소
              </GhostButton>
            </ButtonRow>
          </form>
        ) : (
          <>
            <Title $removed={removed}>{post.title}</Title>
            {authorBlock}
            <Body $removed={removed}>{post.content}</Body>
            {likeBlock}
          </>
        )}
      </Article>

      <CommentsSection>
        <CommentsTitle>댓글 {comments.length}</CommentsTitle>

        {threaded.length === 0 ? (
          <EmptyComments>첫 댓글을 남겨 보세요.</EmptyComments>
        ) : (
          <CommentList>
            {threaded.map(({ node: c, depth }) => {
              const isCAuthor = samePortalUserId(portalUser?.id, c.author?.user_id);
              return (
              <CommentItem key={c.id}>
                <CommentInner $depth={depth}>
                  <CommentCard>
                    <CommentGrid>
                      <CommentGridAv>
                        <SmallAvatar aria-hidden>{c.author?.profile_emoji || '🙂'}</SmallAvatar>
                      </CommentGridAv>
                      <CommentGridAuth>
                        <AuthorStack>
                          {(() => {
                            const { primary, secondary } = getNoticeAuthorDisplay(c.author);
                            return (
                              <>
                                <AuthorName style={{ fontSize: '0.875rem' }}>{primary}</AuthorName>
                                {secondary ? <AuthorLogin>{secondary}</AuthorLogin> : null}
                              </>
                            );
                          })()}
                        </AuthorStack>
                      </CommentGridAuth>
                      <CommentGridAside>
                        <CommentDate>{formatDt(c.created_at)}</CommentDate>
                        {isCAuthor && !c.is_moderation_removed && !removed && editingCommentId !== c.id ? (
                          <>
                            <GhostButton type="button" disabled={loading} onClick={() => beginCommentEdit(c)}>
                              수정
                            </GhostButton>
                            <GhostButton type="button" disabled={loading} onClick={() => handleDeleteComment(c.id)}>
                              삭제
                            </GhostButton>
                          </>
                        ) : null}
                        {moderator && !isCAuthor && !c.is_moderation_removed && !removed ? (
                          <DangerButton type="button" disabled={loading} onClick={() => handleModerateComment(c.id)}>
                            삭제
                          </DangerButton>
                        ) : null}
                        {canComment && !removed && !c.is_moderation_removed && !editingCommentId ? (
                          <GhostButton
                            type="button"
                            onClick={() => {
                              setEditingCommentId(null);
                              setEditCommentDraft('');
                              setReplyTo(c.id);
                              setCommentText('');
                              setCommentError('');
                            }}
                          >
                            답글
                          </GhostButton>
                        ) : null}
                      </CommentGridAside>
                      {editingCommentId === c.id ? (
                        <span style={{ gridArea: 'cgbody', minHeight: '0.25rem' }} aria-hidden />
                      ) : (
                        <CommentGridBody $removed={c.is_moderation_removed}>{c.content}</CommentGridBody>
                      )}
                    </CommentGrid>
                    {editingCommentId === c.id ? (
                      <InlineReplyBox as="form" onSubmit={(e) => handleSaveComment(e, c.id)}>
                        <InlineReplyTitle>댓글 수정</InlineReplyTitle>
                        <CompactTextArea
                          value={editCommentDraft}
                          onChange={(ev) => setEditCommentDraft(ev.target.value)}
                          placeholder="댓글 내용"
                          disabled={loading}
                          maxLength={4000}
                          aria-label="댓글 수정 내용"
                        />
                        <ButtonRow>
                          <PrimaryButton type="submit" disabled={loading}>
                            저장
                          </PrimaryButton>
                          <GhostButton type="button" disabled={loading} onClick={cancelCommentEdit}>
                            취소
                          </GhostButton>
                          {commentError ? (
                            <ErrorText as="span" style={{ margin: 0, flex: '1 1 100%' }}>
                              {commentError}
                            </ErrorText>
                          ) : null}
                        </ButtonRow>
                      </InlineReplyBox>
                    ) : null}
                  </CommentCard>
                  {replyTo === c.id && canComment && !removed && !editingCommentId ? (
                    <InlineReplyBox onSubmit={handleCommentSubmit}>
                      <InlineReplyTitle>답글 작성</InlineReplyTitle>
                      <CompactTextArea
                        value={commentText}
                        onChange={(ev) => setCommentText(ev.target.value)}
                        placeholder="답글을 입력하세요."
                        disabled={loading}
                        aria-label="답글 내용"
                      />
                      <ButtonRow>
                        <PrimaryButton type="submit" disabled={loading}>
                          답글 등록
                        </PrimaryButton>
                        <GhostButton
                          type="button"
                          onClick={() => {
                            setReplyTo(null);
                            setCommentText('');
                            setCommentError('');
                          }}
                        >
                          취소
                        </GhostButton>
                        {commentError ? (
                          <ErrorText as="span" style={{ margin: 0, flex: '1 1 100%' }}>
                            {commentError}
                          </ErrorText>
                        ) : null}
                      </ButtonRow>
                    </InlineReplyBox>
                  ) : null}
                </CommentInner>
              </CommentItem>
              );
            })}
          </CommentList>
        )}

        {removed ? (
          <EmptyComments style={{ marginTop: '0.75rem' }}>
            관리자에 의해 처리된 게시글에는 댓글을 달 수 없습니다.
          </EmptyComments>
        ) : replyTo && !editingCommentId ? (
          <ReplyBottomHint>
            <span>답글 입력란이 열려 있어요.</span>
            <GhostButton
              type="button"
              onClick={() => {
                setReplyTo(null);
                setCommentText('');
                setCommentError('');
              }}
            >
              취소하고 댓글 작성
            </GhostButton>
          </ReplyBottomHint>
        ) : editingCommentId ? (
          <ReplyBottomHint>
            <span>댓글 수정 중이에요.</span>
            <GhostButton type="button" onClick={cancelCommentEdit}>
              수정 취소
            </GhostButton>
          </ReplyBottomHint>
        ) : canComment ? (
          <CommentForm onSubmit={handleCommentSubmit}>
            <TextArea
              value={commentText}
              onChange={(ev) => setCommentText(ev.target.value)}
              placeholder="댓글을 입력하세요."
              disabled={loading}
            />
            <ButtonRow>
              <PrimaryButton type="submit" disabled={loading}>
                댓글 등록
              </PrimaryButton>
              {commentError ? <ErrorText as="span">{commentError}</ErrorText> : null}
            </ButtonRow>
          </CommentForm>
        ) : (
          <EmptyComments style={{ marginTop: '0.75rem' }}>
            댓글을 작성할 수 있는 권한이 없어요.
          </EmptyComments>
        )}
      </CommentsSection>
    </Wrapper>
  );
};

export default PortalBoardDetailPage;
